import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@constants/routes';

interface ElearningWpOnSitePage {
  route: string;
  enabled: boolean;
}

const ELEARNING_WP_ONSITE_FLOW: ElearningWpOnSitePage[] = [
  { route: ROUTES.EWOS_LOGIN, enabled: true },
  { route: ROUTES.EWOS_MVE, enabled: true },
  { route: ROUTES.EWOS_DOB, enabled: true },
  { route: ROUTES.EWOS_WELCOME, enabled: true },
  { route: ROUTES.EWOS_REGISTER, enabled: true },
  { route: ROUTES.EWOS_VIDEO, enabled: true },
  { route: ROUTES.EWOS_FINAL, enabled: true }
];

interface ElearningWpOnSiteFlowContextValue {
  currentPage: ElearningWpOnSitePage | undefined;
  canGoNext: boolean;
  canGoBack: boolean;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;
}

const ElearningWpOnSiteFlowContext =
  createContext<ElearningWpOnSiteFlowContextValue | null>(null);

interface ElearningWpOnSiteFlowProviderProps {
  children: React.ReactNode;
}

function ElearningWpOnSiteFlowProvider({
  children
}: ElearningWpOnSiteFlowProviderProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const allowedPages = useMemo(
    () => ELEARNING_WP_ONSITE_FLOW.filter((p) => p.enabled),
    []
  );
  const currentIndex = useMemo(
    () => allowedPages.findIndex((p) => p.route === pathname),
    [allowedPages, pathname]
  );

  const goNext = useCallback(() => {
    const next = allowedPages[currentIndex + 1];
    if (next) navigate(next.route);
  }, [allowedPages, currentIndex, navigate]);

  const goBack = useCallback(() => {
    const prev = allowedPages[currentIndex - 1];
    if (prev) navigate(prev.route);
  }, [allowedPages, currentIndex, navigate]);

  const reset = useCallback(() => {
    const start = allowedPages[0];
    if (start) navigate(ROUTES.EWOS_WELCOME);
  }, [allowedPages, navigate]);

  const value = useMemo(
    () => ({
      currentPage: allowedPages[currentIndex],
      canGoNext: !!allowedPages[currentIndex + 1],
      canGoBack: !!allowedPages[currentIndex - 1],
      goNext,
      goBack,
      reset
    }),
    [allowedPages, currentIndex, goNext, goBack, reset]
  );

  return (
    <ElearningWpOnSiteFlowContext.Provider value={value}>
      {children}
    </ElearningWpOnSiteFlowContext.Provider>
  );
}

function useElearningWpOnSiteFlow(): ElearningWpOnSiteFlowContextValue {
  const ctx = useContext(ElearningWpOnSiteFlowContext);
  if (!ctx)
    throw new Error(
      'useElearningWpOnSiteFlow must be used within ElearningWpOnSiteFlowProvider'
    );
  return ctx;
}

export { ElearningWpOnSiteFlowProvider, useElearningWpOnSiteFlow };
export type { ElearningWpOnSiteFlowContextValue, ElearningWpOnSitePage };
